import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  CalculateOutlined,
  HomeWorkOutlined,
  PaymentOutlined,
  ReceiptLongOutlined,
  SaveOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import { blue, grey } from "@mui/material/colors";
import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import DollarInput from "../../../../components/inputs/DollarInput";

import { useLiquidityHook } from "../../../../hooks/useLiquidityHook";
import { loanFetchedDataRequiredLiquiditySet } from "../../../../redux/reducers/loanFetchedDataReducer";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import LiquidityCardSectionHeader from "./LiquidityCardSectionHeader";
import Htag from "../../../../components/typography/Htag";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import { isPurchaseLikeLoan } from "../../../../utils/loans/delayedPurchase";

const RequiredLiquidityCard = ({ local, setLocal }) => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const requiredLiquidity = useSelector(
    (state) => state.loanFetchedData?.requiredLiquidity,
  );
  const { loading, updateRequiredLiquidity } = useLiquidityHook();

  const setField = useCallback(
    (key, value) => {
      setLocal((prev) => ({ ...(prev || {}), [key]: value }));
    },
    [setLocal],
  );

  const isDirty = useMemo(
    () =>
      !isNil(local) &&
      !isNil(requiredLiquidity) &&
      !isEqual(local, requiredLiquidity),
    [local, requiredLiquidity],
  );

  const handleSave = useCallback(() => {
    if (!loan?._id || !isDirty) return;
    updateRequiredLiquidity({
      dealId: loan._id,
      data: local,
      onSuccessFn: (data) => {
        // Sync local in the same callback as the Redux dispatch so isDirty
        // flips to false on the same render — otherwise the parent's
        // requiredLiquidity-effect runs a tick later and "Unsaved changes"
        // lingers for a frame after a successful save.
        dispatch(loanFetchedDataRequiredLiquiditySet(data));
        setLocal(data);
      },
    });
  }, [
    loan?._id,
    isDirty,
    local,
    updateRequiredLiquidity,
    dispatch,
    setLocal,
  ]);

  const handleCancel = useCallback(() => {
    if (!isNil(requiredLiquidity)) setLocal(requiredLiquidity);
  }, [requiredLiquidity, setLocal]);

  // Purchase, or a delayed purchase (cash-out + flag / legacy purpose).
  const isLoanPurchase = useMemo(
    () => isPurchaseLikeLoan(loan),
    [loan],
  );

  const months = Number(local?.monthsOfPaymentsRequired) || 0;
  const monthlyPayment = Number(local?.monthlyPaymentAmount) || 0;
  const closingCosts = Number(local?.closingCostsNotFromProceeds) || 0;
  const downPayment = Number(local?.downPayment) || 0;

  const paymentsTotal = months * monthlyPayment;
  const otherCashTotal = closingCosts + downPayment;
  const grandTotal = paymentsTotal + otherCashTotal;

  return (
    <Box>
      <LiquidityCardSectionHeader
        title="Required liquidity"
        subTitle="Minimum liquidity the borrower must demonstrate at closing."
        endContent={
          isDirty ? (
            <Flx ac g={1}>
              <Txt sx={{ fontSize: 11, color: grey[500] }}>Unsaved changes</Txt>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                disabled={loading}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveOutlined />}
                loading={loading}
                onClick={handleSave}
              >
                Save
              </Button>
            </Flx>
          ) : null
        }
      />

      {local && (
        <Box
          sx={{
            borderRadius: 2,
            // background: "#fafafb",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          {/* Inline equation summary — replaces the previous side-panel that
              showed the same numbers in three places (two subtotal tiles, a
              line-item breakdown, and a total row). One line, all the math. */}
          <RequiredTotalSummary
            paymentsTotal={paymentsTotal}
            otherCashTotal={otherCashTotal}
            months={months}
            monthlyPayment={monthlyPayment}
            downPayment={downPayment}
            closingCosts={closingCosts}
            grandTotal={grandTotal}
          />

          <Divider />

          <Flx column gap={3} sx={{ p: 2.5, px: 3.5 }}>
            <SubSection
              title="Payment coverage"
              description="How many months of P&I the borrower must cover out of pocket."
            >
              <FlxGrid gap={4.5}>
                <MonthsOfPaymentsField local={local} setField={setField} />
                <PaymentAmountField local={local} setField={setField} />
              </FlxGrid>
            </SubSection>

            <Divider />

            <SubSection
              title="Other required cash"
              description="Additional funds the borrower brings to close, separate from loan proceeds."
            >
              <FlxGrid gap={4.5}>
                <CostField
                  icon={<ReceiptLongOutlined />}
                  label="Closing costs not paid from proceeds"
                  helper="Portion of closing costs the borrower covers out of pocket."
                  fieldKey="closingCostsNotFromProceeds"
                  toggleKey="autoPullClosingCosts"
                  local={local}
                  setField={setField}
                />

                {isLoanPurchase && (
                  <CostField
                    icon={<HomeWorkOutlined />}
                    label="Down payment"
                    helper="Borrower's down payment contribution."
                    fieldKey="downPayment"
                    toggleKey="autoPullDownPayment"
                    local={local}
                    setField={setField}
                  />
                )}
              </FlxGrid>
            </SubSection>
          </Flx>
        </Box>
      )}
    </Box>
  );
};

/* ───────────────── inline equation summary ───────────────── */

const RequiredTotalSummary = ({
  paymentsTotal,
  otherCashTotal,
  months,
  monthlyPayment,
  grandTotal,
  downPayment,
  closingCosts,
}) => {
  const hasReserves = paymentsTotal > 0;
  const hasOther = otherCashTotal > 0;
  const reservesFormula = `${months} mo × ${formatDollar(monthlyPayment)} monthly P&I payment`;
  const closingFormula = `${formatDollar(downPayment)} borrower contribution + ${formatDollar(closingCosts)} down payment`;

  // const reservesFormula =
  //   months > 0 && monthlyPayment > 0
  //     ? `${months} mo × ${formatDollar(monthlyPayment)}`
  //     : null;

  return (
    <Flx
      ac
      jb
      g={2}
      wrap
      sx={{
        px: 2.5,
        py: 1.75,
        // background: "#fff",
        background: "#fafafb",
      }}
    >
      <Flx ac g={4} wrap sx={{ minWidth: 0 }}>
        {hasReserves ? (
          <EquationTerm
            label="reserves"
            value={paymentsTotal}
            sublabel={reservesFormula}
          />
        ) : (
          <EquationTerm label="reserves" value={0} sublabel={reservesFormula} />
        )}
        <Operator>+</Operator>
        {/* {hasReserves && hasOther && <Operator>+</Operator>} */}
        {hasOther ? (
          <EquationTerm
            label="closing"
            value={otherCashTotal}
            sublabel={closingFormula}
          />
        ) : (
          <EquationTerm label="closing" value={0} sublabel={closingFormula} />
        )}
      </Flx>
      <Operator>=</Operator>
      <Flx column sx={{ alignItems: "flex-end" }}>
        <Txt
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            color: grey[600],
            textTransform: "uppercase",
            letterSpacing: 0.4,
            lineHeight: 1.1,
          }}
        >
          Required total
        </Txt>
        <Txt
          sx={{
            fontSize: 20,
            fontWeight: 700,
            color: "#0f172a",
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.2,
          }}
        >
          {formatDollar(grandTotal)}
        </Txt>
      </Flx>
    </Flx>
  );
};

const EquationTerm = ({ label, value, sublabel }) => (
  <Flx column sx={{ minWidth: 0 }}>
    <Flx ac g={0.5} sx={{ minWidth: 0 }}>
      <Txt
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: "#0f172a",
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.1,
        }}
      >
        {formatDollar(value)}
      </Txt>
      <Txt sx={{ fontSize: 11, color: grey[600] }}>{label}</Txt>
    </Flx>
    {sublabel && (
      <Txt
        sx={{
          fontSize: 10,
          color: grey[500],
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1.3,
          ml: 0.1,
        }}
      >
        {sublabel}
      </Txt>
    )}
  </Flx>
);
// const EquationTerm = ({ label, value, sublabel }) => (
//   <Flx column sx={{ minWidth: 0 }}>
//     <Flx ac g={0.5} sx={{ minWidth: 0 }}>
//       <Txt
//         sx={{
//           fontSize: 14,
//           fontWeight: 700,
//           color: "#0f172a",
//           fontVariantNumeric: "tabular-nums",
//           lineHeight: 1.1,
//         }}
//       >
//         {formatDollar(value)}
//       </Txt>
//       <Txt sx={{ fontSize: 11, color: grey[600] }}>{label}</Txt>
//     </Flx>
//     {sublabel && (
//       <Txt
//         sx={{
//           fontSize: 10,
//           color: grey[500],
//           fontVariantNumeric: "tabular-nums",
//           lineHeight: 1.3,
//           ml: 0.1,
//         }}
//       >
//         {sublabel}
//       </Txt>
//     )}
//   </Flx>
// );

const Operator = ({ children }) => (
  <Txt
    sx={{
      fontSize: 28,
      // fontSize: 14,
      fontWeight: 600,
      color: grey[400],
      fontVariantNumeric: "tabular-nums",
    }}
  >
    {children}
  </Txt>
);

/* ───────────────── sub-sections ───────────────── */

const SubSection = ({ title, description, children }) => (
  <Box sx={{ py: 1.5 }}>
    <Flx column g={0.25} sx={{ mb: 1.75 }}>
      <Htag h3 sx={{ fontSize: 14, color: "#212121", fontWeight: 600 }}>
        {title}
      </Htag>
      {description && (
        <Txt sx={{ fontSize: 12, color: grey[600] }}>{description}</Txt>
      )}
    </Flx>
    {children}
  </Box>
);

/* ───────────────── fields ───────────────── */

const MonthsOfPaymentsField = ({ local, setField }) => (
  <FieldShell
    icon={<CalculateOutlined />}
    label="Months of payments required"
    helper="Number of monthly P&I payments of coverage required."
  >
    <TextField
      size="small"
      fullWidth
      type="number"
      placeholder="0"
      value={local.monthsOfPaymentsRequired ?? ""}
      onChange={(e) =>
        setField(
          "monthsOfPaymentsRequired",
          e.target.value === "" ? null : Number(e.target.value),
        )
      }
      InputProps={{
        endAdornment: (
          <InputAdornment
            sx={{
              ".MuiTypography-root": {
                fontSize: "12px !important",
              },
            }}
            position="end"
          >
            months
          </InputAdornment>
        ),
      }}
    />
  </FieldShell>
);

const PaymentAmountField = ({ local, setField }) => (
  <FieldShell
    icon={<PaymentOutlined />}
    label="Monthly payment amount"
    helper="Monthly principal & interest payment amount."
    rightContent={
      <AutoPullIndicator
        active={!!local.autoPullMonthlyPayment}
        onToggle={(v) => setField("autoPullMonthlyPayment", v)}
      />
    }
  >
    <DollarInput
      size="small"
      value={local.monthlyPaymentAmount ?? ""}
      disabled={local.autoPullMonthlyPayment}
      onChange={(value) =>
        setField("monthlyPaymentAmount", isNil(value) ? null : value)
      }
    />
  </FieldShell>
);

const CostField = ({
  icon,
  label,
  helper,
  fieldKey,
  toggleKey,
  local,
  setField,
}) => (
  <FieldShell
    icon={icon}
    label={label}
    helper={helper}
    rightContent={
      <AutoPullIndicator
        active={!!local[toggleKey]}
        onToggle={(v) => setField(toggleKey, v)}
      />
    }
  >
    <DollarInput
      size="small"
      value={local[fieldKey] ?? ""}
      disabled={local[toggleKey]}
      onChange={(value) => setField(fieldKey, isNil(value) ? null : value)}
    />
  </FieldShell>
);

const FieldShell = ({ icon, label, helper, rightContent, children }) => (
  <Box
    sx={{
      ".MuiInputBase-root": {
        background: "#ffffff",
      },
    }}
  >
    <Flx ac jb g={1} sx={{ mb: 0.5 }}>
      <Flx ac g={0.75} sx={{ minWidth: 0, minHeight: 24 }}>
        <Box
          sx={{
            color: grey[600],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            "& svg": { fontSize: 14 },
          }}
        >
          {icon}
        </Box>
        <Txt
          sx={{
            fontSize: 12,
            fontWeight: 400,

            color: "#212121",

            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Txt>
      </Flx>
      {rightContent}
    </Flx>
    {children}
    {helper && (
      <Txt sx={{ fontSize: 10.5, color: grey[500], mt: 0.5 }}>{helper}</Txt>
    )}
  </Box>
);

/* ───────────────── auto-pull indicator ─────────────────
   Replaces the Switch + "Auto-pull" pair. When active, shows as a clickable
   label "Auto from loan terms" — same affordance as a chip. Click to
   disconnect. When inactive, shows a muted "Auto-fill from loan" link.
   Reads more honestly: "where is this number coming from" instead of
   "is this toggle on or off". */
const AutoPullIndicator = ({ active, onToggle }) => {
  const handleClick = () => onToggle(!active);
  if (active) {
    return (
      <Tooltip title="Click to disconnect and edit manually">
        <Box
          component="button"
          type="button"
          onClick={handleClick}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            background: blue[50],
            color: blue[700],
            border: `1px solid ${blue[100]}`,
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
            fontSize: 10.5,
            fontWeight: 500,
            cursor: "pointer",
            lineHeight: 1.4,
            "&:hover": { background: blue[100] },
            "& svg": { fontSize: 12 },
          }}
        >
          <SyncOutlined />
          Auto from loan
        </Box>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Pull this value from loan terms">
      <Box
        component="button"
        type="button"
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          background: "transparent",
          color: grey[500],
          border: "1px solid transparent",
          borderRadius: 1,
          px: 0.75,
          py: 0.25,
          fontSize: 10.5,
          fontWeight: 500,
          cursor: "pointer",
          lineHeight: 1.4,
          "&:hover": {
            color: blue[700],
            background: blue[50],
            borderColor: blue[100],
          },
          "& svg": { fontSize: 12 },
        }}
      >
        <SyncOutlined />
        Auto-fill
      </Box>
    </Tooltip>
  );
};

export default RequiredLiquidityCard;
